import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import StatCard from '@/components/dashboard/StatCard';
import DateRangeFilter from '@/components/governance/DateRangeFilter';
import type { DateRange } from '@/components/governance/DateRangeFilter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertTriangle,
  TrendingDown,
  DollarSign,
  Package,
  XCircle,
  RotateCcw,
} from 'lucide-react';
import { subDays } from 'date-fns';
import { useQualityIntelligence, type QualityIntelligenceFilters } from '@/hooks/use-quality-intelligence';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (v: number): string => {
  if (v >= 1_000_000) return `Br ${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `Br ${(v / 1_000).toFixed(1)}K`;
  return `Br ${v.toFixed(0)}`;
};

const formatKg = (v: number): string => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M kg`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K kg`;
  return `${v.toFixed(0)} kg`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const TableSkeletonRows = ({ cols, rows = 5 }: { cols: number; rows?: number }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: cols }).map((__, j) => (
          <TableCell key={j}>
            <Skeleton className="h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const KpiSkeleton = () => (
  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="dashboard-card p-4 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-3 w-24" />
      </div>
    ))}
  </div>
);

// ─── Default date range (last 90 days) ───────────────────────────────────────

const DEFAULT_RANGE: DateRange = {
  startDate: subDays(new Date(), 90),
  endDate: new Date(),
};

// ─── Main page ────────────────────────────────────────────────────────────────

const QualityIntelligenceDashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  
  // Separate draft and applied filter states
  const [filterDraft, setFilterDraft] = useState<QualityIntelligenceFilters>({
    entityType: 'all',
  });
  const [filterApplied, setFilterApplied] = useState<QualityIntelligenceFilters>({
    entityType: 'all',
  });

  const { data, isLoading, isError } = useQualityIntelligence(
    dateRange.startDate,
    dateRange.endDate,
    filterApplied, // Use applied filters for data fetching
    filterDraft.entityType // Pass draft entity type for dropdown population
  );

  const handleEntityTypeFilter = (value: string) => {
    setFilterDraft(prev => ({
      ...prev,
      entityType: value,
      entityName: undefined, // Reset entity name when type changes
    }));
  };

  const handleEntityNameFilter = (value: string) => {
    setFilterDraft(prev => ({
      ...prev,
      entityName: value === 'all' ? undefined : value,
    }));
  };

  const handleProductFilter = (value: string) => {
    setFilterDraft(prev => ({
      ...prev,
      productName: value === 'all' ? undefined : value,
    }));
  };

  const handleApplyFilters = () => {
    setFilterApplied(filterDraft);
  };

  const handleResetFilters = () => {
    const resetFilters = { entityType: 'all' };
    setFilterDraft(resetFilters);
    setFilterApplied(resetFilters);
  };

  const hasActiveFilters = filterApplied.entityType !== 'all' || filterApplied.entityName || filterApplied.productName;
  const hasUnappliedChanges = JSON.stringify(filterDraft) !== JSON.stringify(filterApplied);

  // ── Error state ──
  if (isError) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center space-y-2">
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <p className="text-lg font-semibold">Failed to load quality intelligence data</p>
            <p className="text-sm text-muted-foreground">Check your connection and try refreshing.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const summary = data?.summary;
  const supplierAggregation = data?.supplierAggregation ?? [];
  const productAggregation = data?.productAggregation ?? [];
  const allSupplierNames = data?.allSupplierNames ?? [];
  const allProductNames = data?.allProductNames ?? [];
  const allEntityNames = data?.allEntityNames ?? [];

  // Get entity type label
  const getEntityTypeLabel = () => {
    if (!filterDraft.entityType || filterDraft.entityType === 'all') return 'All Entities';
    return filterDraft.entityType.charAt(0).toUpperCase() + filterDraft.entityType.slice(1);
  };

  // Calculate gross damage per supplier from raw records (since service doesn't aggregate it)
  // Gross Damage = damage_kg (before compensation)
  // We need to recalculate from net_damage_kg + compensation_kg
  const calculateGrossDamage = (netDamage: number, compensation: number) => {
    return netDamage + compensation;
  };

  return (
    <Layout>
      <div className="space-y-5">
        {/* ── Page header ── */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Quality Intelligence Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comprehensive damage and compensation analysis across suppliers and products.
          </p>
        </div>

        {/* ── Filter row ── */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
          </div>

          <div className="w-[180px]">
            <label className="text-sm font-medium mb-1.5 block">Entity Type</label>
            <Select
              value={filterDraft.entityType || 'all'}
              onValueChange={handleEntityTypeFilter}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="farm">Farm</SelectItem>
                <SelectItem value="supplier">Supplier</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="w-[200px]">
            <label className="text-sm font-medium mb-1.5 block">Entity Name</label>
            <Select
              value={filterDraft.entityName || 'all'}
              onValueChange={handleEntityNameFilter}
              disabled={isLoading || !allEntityNames.length}
            >
              <SelectTrigger>
                <SelectValue placeholder={`All ${getEntityTypeLabel()}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All {getEntityTypeLabel()}</SelectItem>
                {allEntityNames.map(name => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-[200px]">
            <label className="text-sm font-medium mb-1.5 block">Product</label>
            <Select
              value={filterDraft.productName || 'all'}
              onValueChange={handleProductFilter}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Products" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {allProductNames.map(name => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant={hasUnappliedChanges ? "default" : "outline"}
            size="default"
            onClick={handleApplyFilters}
            disabled={isLoading || !hasUnappliedChanges}
          >
            Apply Filters
          </Button>

          {hasActiveFilters && (
            <Button
              variant="outline"
              size="default"
              onClick={handleResetFilters}
              disabled={isLoading}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          )}
        </div>

        {/* ── KPI row (5 cards) ── */}
        {isLoading ? (
          <KpiSkeleton />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <StatCard
              title="Total Net Damage"
              value={formatKg(summary?.total_net_damage_kg ?? 0)}
              icon={<TrendingDown className="h-5 w-5 text-red-500" />}
              className="border-red-200 bg-red-50/40"
            />
            <StatCard
              title="Total Compensation"
              value={formatKg(summary?.total_compensation_kg ?? 0)}
              icon={<Package className="h-5 w-5 text-green-500" />}
            />
            <StatCard
              title="Total Damage Value"
              value={formatCurrency(summary?.total_damage_value ?? 0)}
              icon={<DollarSign className="h-5 w-5 text-red-500" />}
              className="border-red-200 bg-red-50/40"
            />
            <StatCard
              title="Overall Damage Rate"
              value={`${(summary?.damage_rate ?? 0).toFixed(2)}%`}
              icon={<AlertTriangle className="h-5 w-5 text-yellow-500" />}
              className={
                (summary?.damage_rate ?? 0) > 10
                  ? 'border-red-200 bg-red-50/40'
                  : (summary?.damage_rate ?? 0) > 5
                    ? 'border-yellow-200 bg-yellow-50/40'
                    : ''
              }
            />
            <StatCard
              title="Total Rejected Items"
              value={String(summary?.total_rejected_items ?? 0)}
              icon={<XCircle className="h-5 w-5 text-red-500" />}
            />
          </div>
        )}

        {/* ── Damage Analysis per Source Name Table ── */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Damage Analysis per Source Name</CardTitle>
            <p className="text-xs text-muted-foreground">
              All sources (farms, suppliers, agents) ranked by net damage
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source Name</TableHead>
                  <TableHead>Source Type</TableHead>
                  <TableHead className="text-right">Procurements</TableHead>
                  <TableHead className="text-right">Purchased (kg)</TableHead>
                  <TableHead className="text-right">Gross Damage (kg)</TableHead>
                  <TableHead className="text-right">Gross Damage %</TableHead>
                  <TableHead className="text-right">Compensation (kg)</TableHead>
                  <TableHead className="text-right">Compensation %</TableHead>
                  <TableHead className="text-right">Net Damage (kg)</TableHead>
                  <TableHead className="text-right">Net Damage %</TableHead>
                  <TableHead className="text-right">Rejected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableSkeletonRows cols={11} />
                ) : supplierAggregation.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-10">
                      No damage records found for any source in the selected date range.
                    </TableCell>
                  </TableRow>
                ) : (
                  supplierAggregation.map((supplier) => {
                    const grossDamageKg = calculateGrossDamage(supplier.total_net_damage_kg, supplier.total_compensation_kg);
                    const grossDamagePercent = supplier.total_ordered_kg > 0 
                      ? (grossDamageKg / supplier.total_ordered_kg) * 100 
                      : 0;
                    const compensationPercent = supplier.total_ordered_kg > 0 
                      ? (supplier.total_compensation_kg / supplier.total_ordered_kg) * 100 
                      : 0;
                    const netDamagePercent = supplier.total_ordered_kg > 0 
                      ? (supplier.total_net_damage_kg / supplier.total_ordered_kg) * 100 
                      : 0;
                    
                    return (
                      <TableRow key={`${supplier.source_id}-${supplier.source_type}`} className="hover:bg-muted/30">
                        <TableCell className="font-medium">{supplier.supplier_name}</TableCell>
                        <TableCell className="text-muted-foreground capitalize">{supplier.source_type}</TableCell>
                        <TableCell className="text-right">{supplier.procurement_count}</TableCell>
                        <TableCell className="text-right font-medium">{formatKg(supplier.total_ordered_kg)}</TableCell>
                        <TableCell className="text-right font-semibold text-orange-600">
                          {formatKg(grossDamageKg)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${
                            grossDamagePercent > 10 ? 'text-red-600' : grossDamagePercent > 5 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {grossDamagePercent.toFixed(2)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatKg(supplier.total_compensation_kg)}
                        </TableCell>
                        <TableCell className="text-right text-green-600 font-medium">
                          {compensationPercent.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatKg(supplier.total_net_damage_kg)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={`font-semibold ${
                              netDamagePercent > 10
                                ? 'text-red-600'
                                : netDamagePercent > 5
                                  ? 'text-yellow-600'
                                  : 'text-green-600'
                            }`}
                          >
                            {netDamagePercent.toFixed(2)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{supplier.rejected_count}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* ── Product Damage Table ── */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Product Damage Analysis</CardTitle>
            <p className="text-xs text-muted-foreground">
              Products ranked by net damage with financial impact
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Records</TableHead>
                  <TableHead className="text-right">Purchased (kg)</TableHead>
                  <TableHead className="text-right">Gross Damage (kg)</TableHead>
                  <TableHead className="text-right">Gross Damage %</TableHead>
                  <TableHead className="text-right">Compensation (kg)</TableHead>
                  <TableHead className="text-right">Compensation %</TableHead>
                  <TableHead className="text-right">Net Damage (kg)</TableHead>
                  <TableHead className="text-right">Net Damage %</TableHead>
                  <TableHead className="text-right">Damage Value</TableHead>
                  <TableHead className="text-right">Rejected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableSkeletonRows cols={11} />
                ) : productAggregation.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-10">
                      No product damage records found in the selected date range.
                    </TableCell>
                  </TableRow>
                ) : (
                  productAggregation.map((product) => {
                    // Use backend-calculated gross damage
                    const grossDamageKg = product.total_gross_damage_kg;
                    const grossDamagePercent = product.total_ordered_kg > 0 
                      ? (grossDamageKg / product.total_ordered_kg) * 100 
                      : 0;
                    const compensationPercent = product.total_ordered_kg > 0 
                      ? (product.total_compensation_kg / product.total_ordered_kg) * 100 
                      : 0;
                    const netDamagePercent = product.total_ordered_kg > 0 
                      ? (product.total_net_damage_kg / product.total_ordered_kg) * 100 
                      : 0;
                    
                    return (
                      <TableRow key={product.product_name} className="hover:bg-muted/30">
                        <TableCell className="font-medium">{product.product_name}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{product.record_count}</TableCell>
                        <TableCell className="text-right font-medium">{formatKg(product.total_ordered_kg)}</TableCell>
                        <TableCell className="text-right font-semibold text-orange-600">
                          {formatKg(grossDamageKg)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${
                            grossDamagePercent > 10 ? 'text-red-600' : grossDamagePercent > 5 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {grossDamagePercent.toFixed(2)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatKg(product.total_compensation_kg)}
                        </TableCell>
                        <TableCell className="text-right text-green-600 font-medium">
                          {compensationPercent.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatKg(product.total_net_damage_kg)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${
                            netDamagePercent > 10 ? 'text-red-600' : netDamagePercent > 5 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {netDamagePercent.toFixed(2)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatCurrency(product.damage_value)}
                        </TableCell>
                        <TableCell className="text-right">{product.rejected_count}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-right pb-2">
          Data includes completed/approved/paid procurements with net financial loss (damage &gt; compensation).
        </p>
      </div>
    </Layout>
  );
};

export default QualityIntelligenceDashboard;
